import React from 'react';
import { FormattedMessage } from 'react-intl';
import PropTypes from 'prop-types';
import { removeTimeZoneFromDate } from 'utils/dateUtil';
import messages from '../../messages';
import { Button } from '../../../components/Button';
import Textbox from '../../../components/Textbox';
import CurrencySelector from '../../../components/CurrencySelector';
import DatePicker from '../../../components/DatePicker/index';
import { checkDateOnly, required } from '../../../../../utils/validation/validator';
import { Validate, ErrorMessage } from '../../../../../components/Validation';
import ExpenseForSelect from '../../../components/ExpenseForSelect';

export class HealthExpenseItem extends React.Component {
  onChange = (propertyName) => (event) => {
    const target = event && event.target;
    const value = target ? (target.type === 'checkbox' ? target.checked : target.value) : event;
    const item = { ...this.props.item, [propertyName]: value };
    this.change(item);
  };

  onRemoveClick = () => {
    if (this.props.onRemove) {
      this.props.onRemove(this.props.item.id);
    }
  };

  handleDayChange = (propertyName) => (date) => {
    const localDate = removeTimeZoneFromDate(date);
    const item = { ...this.props.item, [propertyName]: localDate };
    this.change(item);
  };

  change = (item) => {
    if (this.props.onChange) {
      this.props.onChange(item);
    }
  };

  handleCurrencyChange = (value) => {
    const item = {
      ...this.props.item,
      amountClaimedCurrency: value,
      currency: value,
    };
    this.change(item);
  };

  handleChangeInsureds = (selectedClaimFor) => {
    if (selectedClaimFor !== undefined) {
      const item = { ...this.props.item, selectedClaimFor };
      this.change(item);
    }
  };

  render() {
    const { item, disabled, removable, validatorGroup, validationCounter, insuredsList, intl } = this.props;
    const selectedClaimForList = !!item.selectedClaimFor && typeof item.selectedClaimFor.toJS === 'function'
      ? item.selectedClaimFor.toJS()
      : item.selectedClaimFor || [];

    return (
      <div className="expenses">
        <div className="row align-items-center">
          {!disabled && removable && (
            <div className="form-group col-auto">
              <Button className="base-button primary-button" onClick={this.onRemoveClick}>Remove</Button>
            </div>
          )}
        </div>
        <div className="row">
          <div className="form-group col-sm-7 col-md-7">
            <ExpenseForSelect
              validatorGroup={validatorGroup}
              validationCounter={validationCounter}
              defaultValue={selectedClaimForList}
              disabled={disabled}
              options={insuredsList || []}
              onChange={(v) => this.handleChangeInsureds(v)}
              value={selectedClaimForList}
              fieldName={messages.expenseFor}
              tooltipContent={intl ? intl.formatMessage(messages.selectInsuredForExpense) : messages.selectInsuredForExpense.defaultMessage}
              messages={messages}
              placeholder={intl ? intl.formatMessage(messages.selectInsureds) : 'Select Insured(s)'}
              intl={intl}
            />
          </div>
          <div className="form-group col-sm-5 col-md-5" />
        </div>
        <div className="row">
          <div className="form-group col-sm-7 col-md-7">
            <Validate
              validatorLeaveGroup={validatorGroup && validatorGroup.removeValidatorToGroup}
              group={validatorGroup}
              validators={[required]}
              addValidatorToGroup={validatorGroup && validatorGroup.addValidatorToGroup}
              validChangeInGroup={validatorGroup && validatorGroup.validChangeInGroup}
              validationCounter={validationCounter}
              className="w-full"
              defaultValue={item.descriptionOfExpense}
              fieldName={messages.descriptionOfExpense}
            >
              <Textbox
                disabled={disabled}
                name="descriptionOfExpense"
                type="text"
                onChange={this.onChange('descriptionOfExpense')}
                value={item.descriptionOfExpense || ''}
              />
              <ErrorMessage for="required">
                <FormattedMessage
                  {...messages.isRequired}
                  values={{ defaultMessage: intl ? intl.formatMessage(messages.descriptionOfExpense) : messages.descriptionOfExpense.defaultMessage }}
                />
              </ErrorMessage>
            </Validate>
          </div>
          <div className="form-group col-md-5 col-sm-5">
            <Validate
              name="dateOfExpense"
              validators={[checkDateOnly, required]}
              group={validatorGroup}
              addValidatorToGroup={validatorGroup && validatorGroup.addValidatorToGroup}
              validChangeInGroup={validatorGroup && validatorGroup.validChangeInGroup}
              validationCounter={validationCounter}
              validatorLeaveGroup={validatorGroup && validatorGroup.removeValidatorToGroup}
              defaultValue={item.dateOfExpense}
              fieldName={messages.dateOfExpense}
            >
              <DatePicker
                name="dateOfExpense"
                disabled={disabled}
                type="text"
                onDayChange={this.handleDayChange('dateOfExpense')}
                value={item.dateOfExpense || ''}
                placeholder="dd/mm/yyyy"
              />
              <ErrorMessage for="required">
                <FormattedMessage
                  {...messages.isRequired}
                  values={{ defaultMessage: intl ? intl.formatMessage(messages.dateOfExpense) : messages.dateOfExpense.defaultMessage }}
                />
              </ErrorMessage>
              <ErrorMessage for="checkDateOnly">
                <FormattedMessage
                  {...messages.checkEventDate}
                  values={{ defaultMessage: intl ? intl.formatMessage(messages.dateOfExpense) : messages.dateOfExpense.defaultMessage }}
                />
              </ErrorMessage>
            </Validate>
          </div>
        </div>
        <div className="row">
          <div className="form-group col-sm-7 col-md-7">
            <Validate
              validatorLeaveGroup={validatorGroup && validatorGroup.removeValidatorToGroup}
              group={validatorGroup}
              validators={[required]}
              addValidatorToGroup={validatorGroup && validatorGroup.addValidatorToGroup}
              validChangeInGroup={validatorGroup && validatorGroup.validChangeInGroup}
              validationCounter={validationCounter}
              className="w-full"
              defaultValue={item.amountClaimed}
              fieldName={messages.amountClaimed}
            >
              <Textbox
                disabled={disabled}
                name="amountClaimed"
                type="number"
                onChange={this.onChange('amountClaimed')}
                value={item.amountClaimed || ''}
              />
              <ErrorMessage for="required">
                <FormattedMessage
                  {...messages.isRequired}
                  values={{ defaultMessage: intl ? intl.formatMessage(messages.amountClaimed) : messages.amountClaimed.defaultMessage }}
                />
              </ErrorMessage>
            </Validate>
          </div>
          <div className="form-group col-md-5 col-sm-5">
            <Validate
              validatorLeaveGroup={validatorGroup && validatorGroup.removeValidatorToGroup}
              group={validatorGroup}
              validators={[required]}
              addValidatorToGroup={validatorGroup && validatorGroup.addValidatorToGroup}
              validChangeInGroup={validatorGroup && validatorGroup.validChangeInGroup}
              validationCounter={validationCounter}
              className="w-full"
              defaultValue={item.amountClaimedCurrency || item.currency}
              fieldName={messages.currency}
            >
              <CurrencySelector
                onChange={this.handleCurrencyChange}
                disabled={disabled}
                value={item.amountClaimedCurrency || item.currency || ''}
              />
              <ErrorMessage for="required">
                <FormattedMessage
                  {...messages.isRequired}
                  values={{ defaultMessage: intl ? intl.formatMessage(messages.currency) : messages.currency.defaultMessage }}
                />
              </ErrorMessage>
            </Validate>
          </div>
        </div>
        <div className="row">
          <div className="form-group col-sm-12 col-md-12">
            <Validate
              validatorLeaveGroup={validatorGroup && validatorGroup.removeValidatorToGroup}
              group={validatorGroup}
              validators={[required]}
              addValidatorToGroup={validatorGroup && validatorGroup.addValidatorToGroup}
              validChangeInGroup={validatorGroup && validatorGroup.validChangeInGroup}
              validationCounter={validationCounter}
              className="w-full"
              defaultValue={item.providerName}
              fieldName={messages.providerName}
            >
              <Textbox
                disabled={disabled}
                name="providerName"
                type="text"
                onChange={this.onChange('providerName')}
                value={item.providerName || ''}
              />
              <ErrorMessage for="required">
                <FormattedMessage
                  {...messages.isRequired}
                  values={{ defaultMessage: intl ? intl.formatMessage(messages.providerName) : messages.providerName.defaultMessage }}
                />
              </ErrorMessage>
            </Validate>
          </div>
        </div>
      </div>
    );
  }
}

HealthExpenseItem.propTypes = {
  item: PropTypes.object,
  onChange: PropTypes.func,
  onRemove: PropTypes.func,
  index: PropTypes.number,
  disabled: PropTypes.bool,
  removable: PropTypes.bool,
  validatorGroup: PropTypes.object,
  validationCounter: PropTypes.number,
  insuredsList: PropTypes.array,
  intl: PropTypes.object,
};

export default HealthExpenseItem;



// WEBPACK FOOTER //
// ./app/containers/FastCover/Tugo.TripInterruption/components/HealthExpense/HealthExpenseItem.js